import type { GreaterArea } from '@/config/objects/greater-areas';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  StatusPill,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Building, Globe, House, Map, MapPin, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Globe },
  { label: 'Neighborhoods', path: 'neighborhoods', icon: Building },
  { label: 'Hotels', path: 'hotels', icon: House },
  { label: 'Tours', path: 'tours', icon: Map },
  { label: 'Attractions', path: 'attractions', icon: MapPin },
  { label: 'Health', path: 'health', icon: Activity },
];

export function GreaterAreaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: GreaterArea }>({
    queryKey: ['GreaterArea', id],
    queryFn: () => apiClient.get(`/greater-areas/${id}`),
    enabled: !!id,
  });

  const greaterArea = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (greaterAreaId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/greater-areas/${greaterAreaId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['GreaterArea'] });
        navigate('/greater-areas');
      } else {
        throw new Error(response.error || 'Failed to delete greater area');
      }
    },
  });

  const handleDelete = () => {
    if (!greaterArea) return;
    toast.promise(deleteMutation.mutateAsync(greaterArea.id), {
      loading: 'Deleting greater area...',
      success: 'Greater area deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete greater area',
    });
  };

  // Redirect base path to /overview
  useEffect(() => {
    if (id) {
      const basePath = `/greater-areas/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Greater Areas', href: '/greater-areas' },
    { label: greaterArea?.name || 'Greater Area', current: true },
  ];

  const basePath = `/greater-areas/${id}`;
  const imageUrl = greaterArea?.featuredImage?.url ?? null;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Greater Area Not Found</h2>
          <p className="text-muted-foreground">
            The requested greater area could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          greaterArea && (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Greater Area', icon: Globe }}
        title={greaterArea?.name || 'Greater Area'}
        image={{
          url: imageUrl,
          alt: greaterArea?.name || 'Greater Area',
          fallbackIcon: Globe,
        }}
        meta={
          (greaterArea?.city || greaterArea?.state) && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {[greaterArea.city, greaterArea.state].filter(Boolean).join(', ')}
            </span>
          )
        }
        badges={
          greaterArea?.status && <StatusPill status={greaterArea.status} />
        }
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ greaterArea, isLoading }} />
      </DetailPageHero>
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Greater Area"
        description={`Are you sure you want to delete "${greaterArea?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
